import { cloneDeep } from 'lodash';
import { useImmerAtom } from 'jotai-immer';
import { useGroupsCreateStore } from '../pagehooks/useGroupsCreateStore';
import { useGroupsCreateState } from './useGroupsCreateState';
import { createModelGroupAtom, readOnlycreateModelGroupAtom } from '../groups_create_atoms';
import { AdminGroupsModel, EditModelGroupHook, GroupType, StatusVal, VmaxItem } from '../groups_create_types';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import groupsRepo from '../../data/groupsRepo';
import { HttpError422, HttpErrorUpsert, HttpUpsert200 } from '../../../../../global';
import utilSvc from '../../../../core/utilSvc';
import validSvc from '../../../../core/validSvc';
import { ValidPropText } from '../../../../core/components/validation/ValidInputText';
import { createMemberAddDroplist, membersVM, vmaxVendorsVM } from '../groups_create_view_models';
import adminVendorRepo from '../../../../core/repo/adminVendorRepo';

const validateGroupName = (prop: ValidPropText): boolean => {
    const val = prop.val.trim();
    const isThreeOrMore = typeof prop.val === 'string' && val.length > 2;
    const isFiftyOrLess = val.length <= 50;

    if (!isThreeOrMore) {
        prop.err = 'Required, name must be 3 or more characters in length.';
    } else if (!isFiftyOrLess) {
        prop.err = 'Required, name must be a max of 50 characters in length.';
    } else {
        prop.err = '';
    }
    return isFiftyOrLess && isThreeOrMore;
};

export const useGroupsCreateModel = (): EditModelGroupHook => {
    const { state: pageState, setPageMode } = useGroupsCreateStore();
    const { setState: setEditState, waiting, members, memberAddDropList, vmaxPopup } = useGroupsCreateState();
    const [model, setModel] = useImmerAtom(createModelGroupAtom);
    const [readOnlyModel, setReadOnlyModel] = useImmerAtom(readOnlycreateModelGroupAtom);

    const showVmax = pageState.mode === 'UPDATE' && model.Type.val === GroupType.IDN;

    function submit(): void {
        if (saveAcct() && !waiting) {
            submitAsync();
        }
    }

    async function submitAsync(): Promise<void> {
        // API: CREATE / UPDATE
        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const r = (await groupsRepo.upsert(pageState.mode, model)) as HttpUpsert200;

            const successTitle = pageState.mode === 'UPDATE' ? 'Group Saved' : 'Group Created';
            const successMessage = pageState.mode === 'UPDATE' ? undefined : model.Name.val;
            ngDelegate.ok(successTitle, successMessage);

            if (pageState.mode === 'CREATE') {
                setPageMode('UPDATE', r.Entity.Id);
            }
        } catch (err: HttpErrorUpsert | any) {
            console.log('STORE POST ERR', err);
            if (utilSvc.http.isIgnore(err)) {
                return;
            }

            err = err as HttpErrorUpsert;
            if (err.status === 422) {
                // handle 422 as model
                err = err as HttpError422;

                // figure out 409 (this is not correct API should be throwing a 409 status, not 422)
                if (
                    Array.isArray(err.data.Entity.ROOT) &&
                    err.data.Entity.ROOT.length > 0 &&
                    err.data.Entity.ROOT[0] == 'Duplicate'
                ) {
                    setModel(draft => {
                        draft.Name.err = 'name already exists.';
                    });
                } else {
                    setModel(model => {
                        validSvc.http422ToModel(err, model);
                        return model;
                    });
                }
            }
            ngDelegate.ok('Cannot Save', 'Check Properties');
        } finally {
            setReadOnlyModel(() => model);
            setEditState({ waiting: false });
        }
    }

    const setStatus = (status: StatusVal): void => {
        if (waiting) {
            return;
        }
        statusChangeAsync(status);
    };

    async function statusChangeAsync(status: StatusVal): Promise<void> {
        // API: STATUS CHANGE

        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const r = (await groupsRepo.updateStatus(model.Id, status as string)) as HttpUpsert200;
            ngDelegate.ok('Group Status Changed to: ' + status);

            // switch status
            setModel(draft => {
                draft.ObjectState.val = status;
            });
            ngDelegate.ok('Status Changed');
        } catch (err: any) {
            console.log('STORE POST ERR');
            if (utilSvc.http.isIgnore(err)) {
                return;
            }

            // handle 409?
            ngDelegate.ok('Cannot Save', err);
        } finally {
            setEditState({ waiting: false });
        }
    }

    // ACCOUNT
    async function getDetail(id: string): Promise<void> {
        // API: get group
        setEditState({ waiting: true });

        try {
            const r = await groupsRepo.getDetail(id);
            const _members = membersVM(r);
            // get vmax vendors
            const venList = await adminVendorRepo.getList();

            setEditState({
                members: _members,
                memberAddDropList: createMemberAddDroplist(model.Type.val, _members),
                vmaxVendors: vmaxVendorsVM(venList),
            });
        } catch (err: any) {
            // handled upstream, need a catch when using a try/finally
        } finally {
            setEditState({ waiting: false });
        }
    }

    function setPropText(propName: string, val: string | number): void {
        if (!!!model.hasOwnProperty(propName)) {
            throw new Error('groupEditStore.setAcctProp, propName unknown: ' + propName);
        }

        switch (propName) {
            case 'Type':
                // Type cannot be changed if members exist
                if (members.length > 0) {
                    ngDelegate.ok('Cannot Change Type', 'Remove all group members before changing');
                    return;
                }

                if (typeof val === 'string') {
                    setEditState({
                        memberAddDropList: createMemberAddDroplist(val),
                    });
                }
                break;
            case 'ContractFulfillment':
            case 'ContractType':
                // reset vmax items
                setModel(draft => {
                    draft.Threshold = [];
                });
                break;
        }

        setModel(draft => {
            draft[propName].val = val;
            if (draft[propName].err !== '') {
                draft[propName].err = '';
            }
        });
    }

    function validAcct(m: AdminGroupsModel): boolean {
        let result = validateGroupName(m.Name);
        result = result && validSvc.testDroplistText(m.Type as any, true);
        return result;
    }

    function saveAcct(): boolean {
        // validate synchronous outside Immer
        const nextModel = cloneDeep(model) as AdminGroupsModel;

        if (!validAcct(nextModel)) {
            ngDelegate.ok('Cannot Save', 'Check properties');
            setModel(() => nextModel);
            return false;
        }
        return true;
    }

    // MEMBERS
    function memberChange(id: string): void {
        setEditState({ memberAddDropList: { ...memberAddDropList, val: id } });
    }

    function memberAdd(): void {
        const memId = memberAddDropList?.val;

        if (!memId) {
            return;
        }
        memberEdit('ADD', memId);
    }

    function memberRemove(id: string): void {
        memberEdit('REMOVE', id);
    }

    async function memberEdit(action: 'ADD' | 'REMOVE', mem: string): Promise<void> {
        // API: MEMBER ADD/REMOVE
        try {
            await groupsRepo.memberEdit(action, model.Id, mem);
            const editMsg = action == 'ADD' ? 'Member Added' : 'Member Removed';
            ngDelegate.ok(editMsg);

            // refresh (we could avoid the extra API call and keep it dirty UI side)
            getDetail(model.Id);

            setEditState({
                members: membersVM(members),
            });
        } catch (err: any) {
            console.log('MEMBER POST ERR');
            if (utilSvc.http.isIgnore(err)) {
                return;
            }

            // handle 422 as string
            const errMsg = validSvc.http422ToString(err as HttpError422);
            ngDelegate.ok('Cannot Save Member', errMsg);
            return;
        }

        return;
    }

    /**
     * Unused for now while Vmax feature is refactored.
     */
    function vmaxTogglePopup(): void {
        if (!vmaxPopup) {
            // toggle popup
        }
        setEditState({ vmaxPopup: !vmaxPopup });
    }

    // THRESHOLD
    function vmaxThresholdUpdate(items: VmaxItem[]): void {
        setModel(draft => {
            draft.Threshold = items;
        });
    }

    return {
        model,
        readOnlyModel,
        setPropText,
        setStatus,
        submit,
        showVmax,
        saveAcct,
        memberAdd,
        memberChange,
        memberRemove,
        vmaxTogglePopup,
        vmaxThresholdUpdate,
    };
};
